/**
 * Cartridge Entity Tabs
 *
 * Provides support for cartridge-injected tabs on entity detail pages.
 * Uses a tab registry for component lookup with fallback placeholder.
 */
import { Suspense, ComponentType } from 'react'
import { PuzzlePieceIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import * as HeroIcons from '@heroicons/react/24/outline'
import type { CartridgeEntityTabWithSource, CartridgeEntityTab } from '../../hooks/useCartridgeUI'
import { useTranslation } from '../../hooks/useTranslation'

// Tab component registry - cartridges register their tab components here
type TabComponent = ComponentType<TabComponentProps>

export interface TabComponentProps {
  entityId: string
  entityType: CartridgeEntityTab['entities'][number]
  tab: CartridgeEntityTabWithSource
}

const tabRegistry = new Map<string, TabComponent>()

/**
 * Register a tab component for a cartridge
 * Call this at cartridge initialization time
 */
export function registerCartridgeTab(
  cartridgeSlug: string,
  tabKey: string,
  component: TabComponent
) {
  const fullKey = `${cartridgeSlug}:${tabKey}`
  tabRegistry.set(fullKey, component)
}

/**
 * Get a registered tab component
 */
export function getCartridgeTab(
  cartridgeSlug: string,
  tabKey: string
): TabComponent | undefined {
  const fullKey = `${cartridgeSlug}:${tabKey}`
  return tabRegistry.get(fullKey)
}

// Helper to get icon component by name
function getIconByName(iconName: string): typeof PuzzlePieceIcon {
  const pascalCase = iconName
    .split('-')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join('') + 'Icon'

  const icon = (HeroIcons as Record<string, unknown>)[pascalCase]
  if (icon && typeof icon === 'function') {
    return icon as typeof PuzzlePieceIcon
  }

  return PuzzlePieceIcon
}

/**
 * Tab info for rendering in tab bar
 */
export interface CartridgeTabInfo {
  key: string
  label: string
  icon: typeof PuzzlePieceIcon
  cartridgeSlug: string
  tab: CartridgeEntityTabWithSource
}

/**
 * Convert cartridge tabs to tab info for rendering
 */
export function useCartridgeTabInfo(
  tabs: CartridgeEntityTabWithSource[]
): CartridgeTabInfo[] {
  const { t } = useTranslation()

  return tabs.map(tabWithSource => ({
    key: `cartridge:${tabWithSource.cartridgeSlug}:${tabWithSource.tab.key}`,
    label: t(tabWithSource.tab.labelKey as never) || tabWithSource.tab.key,
    icon: getIconByName(tabWithSource.tab.icon),
    cartridgeSlug: tabWithSource.cartridgeSlug,
    tab: tabWithSource,
  }))
}

/**
 * Placeholder shown when tab component is not registered
 */
function TabPlaceholder({ tab }: { tab: CartridgeEntityTabWithSource }) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <PuzzlePieceIcon className="h-16 w-16 text-gray-300 mb-4" />
      <p className="text-sm font-medium text-gray-500">
        {t(tab.tab.labelKey as never) || tab.tab.key}
      </p>
      <p className="text-xs text-gray-400 mt-1">
        {t('cartridges.tabNotLoaded' as never) || `From ${tab.cartridgeSlug} cartridge`}
      </p>
      <p className="text-xs text-gray-300 mt-2">
        Component: {tab.tab.component}
      </p>
    </div>
  )
}

/**
 * Error boundary fallback for tab errors
 */
function TabError({ tab }: { tab: CartridgeEntityTabWithSource }) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <ExclamationTriangleIcon className="h-16 w-16 text-amber-400 mb-4" />
      <p className="text-sm font-medium text-gray-500">
        {t('cartridges.tabError' as never) || 'Tab Error'}
      </p>
      <p className="text-xs text-gray-400 mt-1">
        {tab.tab.key}
      </p>
    </div>
  )
}

/**
 * Loading skeleton for tabs
 */
function TabSkeleton() {
  return (
    <div className="animate-pulse py-8">
      <div className="h-6 bg-gray-200 rounded w-1/4 mb-6"></div>
      <div className="space-y-4">
        <div className="h-4 bg-gray-200 rounded"></div>
        <div className="h-4 bg-gray-200 rounded w-5/6"></div>
        <div className="h-4 bg-gray-200 rounded w-4/6"></div>
        <div className="h-32 bg-gray-100 rounded mt-6"></div>
      </div>
    </div>
  )
}

interface CartridgeTabContentProps {
  entityId: string
  entityType: CartridgeEntityTab['entities'][number]
  tab: CartridgeEntityTabWithSource
}

/**
 * Renders the content of a cartridge tab
 */
export function CartridgeTabContent({ entityId, entityType, tab }: CartridgeTabContentProps) {
  // Look up the registered component
  const TabComponent = getCartridgeTab(tab.cartridgeSlug, tab.tab.key)

  if (!TabComponent) {
    return <TabPlaceholder tab={tab} />
  }

  return (
    <Suspense fallback={<TabSkeleton />}>
      <TabComponent
        entityId={entityId}
        entityType={entityType}
        tab={tab}
      />
    </Suspense>
  )
}

/**
 * Hook to merge static tabs with cartridge tabs
 * Returns merged tab list and helper functions
 */
export function useMergedEntityTabs<T extends { key: string; position?: number }>(
  staticTabs: T[],
  cartridgeTabs: CartridgeEntityTabWithSource[]
): {
  allTabs: (T | CartridgeTabInfo)[]
  isCartridgeTab: (key: string) => boolean
  getCartridgeTab: (key: string) => CartridgeEntityTabWithSource | undefined
} {
  const cartridgeTabInfos = useCartridgeTabInfo(cartridgeTabs)

  // Merge and sort tabs
  const allTabs = [...staticTabs, ...cartridgeTabInfos].sort((a, b) => {
    const posA = 'position' in a ? (a.position ?? 50) : 100
    const posB = 'position' in b ? (b.position ?? 50) : 100
    return posA - posB
  })

  const isCartridgeTab = (key: string): boolean => {
    return key.startsWith('cartridge:')
  }

  const getCartridgeTabData = (key: string): CartridgeEntityTabWithSource | undefined => {
    const info = cartridgeTabInfos.find(t => t.key === key)
    return info?.tab
  }

  return {
    allTabs,
    isCartridgeTab,
    getCartridgeTab: getCartridgeTabData,
  }
}
